import { displayTimeZone } from '../../lib/time';
/**
 * The Reports frame on the page (TOADO-733): the filters as the address holds
 * them, so a link to a report is one a teammate can open, the comparison with
 * the previous period, and CSV of exactly what a report shows.
 */
export const REPORT_PERIODS = [
  { value: 'week', label: 'This week' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'quarter', label: 'This quarter' },
  { value: 'custom', label: 'Custom range' },
] as const;
export type ReportPeriodKind = (typeof REPORT_PERIODS)[number]['value'];

export type ReportFilters = { period: ReportPeriodKind; from: string; to: string; projectId: string };

const isPeriod = (value: string | null): value is ReportPeriodKind => REPORT_PERIODS.some((period) => period.value === value);
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The filters in the address, with the last 30 days and the whole workspace when it says nothing usable. */
export function readFilters(params: URLSearchParams): ReportFilters {
  const period = params.get('period');
  const day = (name: string) => {
    const value = params.get(name) ?? '';
    return DAY.test(value) ? value : '';
  };
  return { period: isPeriod(period) ? period : '30d', from: day('from'), to: day('to'), projectId: params.get('project') ?? '' };
}

/** The address for these filters; only what differs from the defaults is written. */
export function writeFilters(filters: ReportFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.period !== '30d') params.set('period', filters.period);
  if (filters.period === 'custom') {
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
  }
  if (filters.projectId) params.set('project', filters.projectId);
  return params;
}

/** The query a report request sends: the filters, the workspace and the viewer's time zone. */
export function reportQuery(companyId: string, filters: ReportFilters, timeZone = viewerTimeZone()): string {
  const params = new URLSearchParams({ companyId, period: filters.period, tz: timeZone });
  if (filters.period === 'custom') {
    params.set('from', filters.from);
    params.set('to', filters.to);
  }
  if (filters.projectId) params.set('projectId', filters.projectId);
  return params.toString();
}

/** A custom range is asked for only once both days are set and in order. */
export const filtersReady = (filters: ReportFilters) =>
  filters.period !== 'custom' || (!!filters.from && !!filters.to && filters.from <= filters.to);

/**
 * Whether a failed report is worth asking again: a dropped connection or a
 * server error, twice at most. Never what the server will answer the same
 * way: a request it refused (4xx, the plan gate among them) or a report too
 * slow for the period, which only a shorter period helps (Codex review round 1).
 */
export function retryReport(failures: number, error: unknown): boolean {
  const { status, code } = (error ?? {}) as { status?: number; code?: string };
  if (code === 'report_too_slow') return false;
  return failures < 2 && (status === undefined || status >= 500);
}

/** The zone a report's days are read in: the person's own, as every time on the page is (TOADO-904). */
export function viewerTimeZone(): string {
  return displayTimeZone();
}

/** What the period is compared with, in words. */
export function previousLabel(period: ReportPeriodKind, from?: string, to?: string): string {
  switch (period) {
    case 'week': return 'the same point last week';
    case 'quarter': return 'the same point last quarter';
    case '7d': return 'the previous 7 days';
    case '30d': return 'the previous 30 days';
    case 'custom': {
      const days = from && to ? Math.round((Date.parse(to) - Date.parse(from)) / 864e5) + 1 : 0;
      return days === 1 ? 'the day before' : `the previous ${days} days`;
    }
  }
}

/** "up 40% on the previous 30 days", "down 2", "the same as", or "new" when there was nothing before. */
export function comparison(current: number, previous: number, against: string): string {
  if (current === previous) return `the same as ${against}`;
  if (previous === 0) return `up from none in ${against}`;
  const change = Math.round(((current - previous) / previous) * 100);
  return `${change > 0 ? 'up' : 'down'} ${Math.abs(change)}% on ${against}`;
}

/**
 * CSV of a report's rows, as shown. Text is typed by people (project names,
 * page addresses, error messages), so every text cell is quoted, and one a
 * spreadsheet could read as a formula is prefixed with an apostrophe: it
 * starts, after any spaces, with =, +, -, @, a tab or a return, including the
 * full-width forms some spreadsheets also accept (Codex review round 1).
 * Numbers are written as they are.
 */
export function toCsv(header: string[], rows: Array<Array<string | number>>): string {
  const cell = (value: string | number) => {
    if (typeof value === 'number') return String(value);
    const probe = value.normalize('NFKC').trimStart();
    const text = /^[=+\-@\t\r]/.test(probe) || /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
    return `"${text.replace(/"/g, '""')}"`;
  };
  return [header, ...rows].map((row) => row.map(cell).join(',')).join('\r\n') + '\r\n';
}

/** Saves CSV text as a file the browser downloads. */
export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** A time to fix in words: minutes under an hour, hours under two days, days after. */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '-';
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))} min`;
  const hours = minutes / 60;
  if (hours < 48) return `${Math.round(hours * 10) / 10} h`;
  return `${Math.round((hours / 24) * 10) / 10} days`;
}

/**
 * A count as a report shows it, on the page and in its CSV alike: one drawn
 * from a read cut short can only be an undercount, so it carries a "+"
 * ("20000+" is at least 20,000). Every figure goes through this, so no table,
 * chart or export can show a lower bound as exact (TOADO-862, Codex review
 * round 14). The words in a finding say "at least" for the same rule.
 */
export const figure = (n: number, lowerBound: boolean): string => (lowerBound ? `${n}+` : String(n));
